<script>
	import { onMount } from 'svelte';
	import { isValuationStale } from './valuation-age.js';
	import { clearAuthentication, login, logout, register } from './auth.js';
	import { addLiability, getLiabilities, updateLiability, addValuation, getValuations, updateValuation, createAsset, deleteAsset, deleteProfile, getApiBuildInfo, getAssets, getWebBuildInfo, updateAsset, getOwnerships, saveOwnerships } from './api.js';

	export let authenticated = false;
	export let username = null;
	export let roles = [];
	export let authError = null;

	let assets = [];
	let loadingAssets = false;
	let assetsError = null;
	let newAssetName = '';
	let newAssetType = '';
	let newProductType = '';
	let newAssetLocation = '';
	let creatingAsset = false;
	let createAssetError = null;
	let editedAsset = null;
	import { assetCurrency, isForeignCurrencyAccount, netAssetValue } from './currency.js';
	$: valuationCurrency = assetCurrency(editedAsset);
	$: liabilityCurrency = assetCurrency(editedAsset);
	let editedAssetName = '';
	let editedAssetType = '';
	let editedProductType = '';
	let editedAssetLocation = '';
	let valuations = [];
	let liabilities = [];
	let ownerships = [];
	let ownershipError = null;
	let savingOwnerships = false;
	let wealthScope = 'personal';
	let liabilityDate = new Date().toLocaleDateString('en-CA');
	let liabilityAmount = '';
	let liabilityCurrency = 'EUR';
	let liabilityError = null;
	let savingLiability = false;
	let valuationDate = new Date().toLocaleDateString('en-CA');
	let valuationAmount = '';
	let valuationCurrency = 'EUR';
	let valuationError = null;
	let savingValuation = false;
	let editingRecordId = null;
	const formatEuro = (amount, currency = 'EUR') => new Intl.NumberFormat('en-IE', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
	let savingAsset = false;
	let editAssetError = null;
	let currentPage = 'assets';
	let menuOpen = false;
	let confirmation = '';
	let assetDeleteConfirmation = '';
	let deleting = false;
	let deletionError = null;
	let deleted = false;
	let webDockerBuildTimestamp = 'unknown';
	let apiDockerBuildTimestamp = 'unknown';

	$: totalNetWealth = assets.reduce((total, asset) => total + netAssetValue(asset, wealthScope), 0);
	const chartPalettes = [
		{ group: '#176b87', assets: ['#176b87', '#2586a2', '#0e536b', '#45a5b9'] },
		{ group: '#a85c08', assets: ['#a85c08', '#ca7610', '#854606', '#e09928'] },
		{ group: '#47742d', assets: ['#47742d', '#65943d', '#365c27', '#83a94b'] }
	];
	const polar = (radius, angle) => [200 + radius * Math.cos(angle - Math.PI / 2), 200 + radius * Math.sin(angle - Math.PI / 2)];
	function ringPath(inner, outer, start, end) {
		const span = Math.min(end - start, Math.PI * 2 - 0.00001);
		if (span <= 0) return '';
		const a = polar(outer, start), b = polar(outer, start + span);
		const c = polar(inner, start + span), d = polar(inner, start);
		return `M ${a.join(' ')} A ${outer} ${outer} 0 ${span > Math.PI ? 1 : 0} 1 ${b.join(' ')} L ${c.join(' ')} A ${inner} ${inner} 0 ${span > Math.PI ? 1 : 0} 0 ${d.join(' ')} Z`;
	}
	function chartLabel(name, start, end, radius, maxFontSize) {
		const [x, y] = polar(radius, (start + end) / 2);
		const fontSize = Math.min(maxFontSize, 10);
		const availableWidth = Math.min(62, radius * (end - start) - 10);
		const maxCharacters = Math.floor(availableWidth / (fontSize * 0.62));
		const text = maxCharacters < 4 ? '' : name.length > maxCharacters ? name.slice(0, maxCharacters - 1) + '…' : name;
		return { x, y, fontSize, text };
	}
	function buildChart(items, scope) {
		const groups = ['PHYSICAL', 'VIRTUAL', 'OTHER'].map((type, index) => ({
			name: type === 'PHYSICAL' ? 'Physical' : type === 'VIRTUAL' ? 'Virtual' : 'Other',
			color: chartPalettes[index].group,
			assetColors: chartPalettes[index].assets,
			items: items.filter(asset => (asset.type === 'PHYSICAL' ? 'PHYSICAL' : asset.type === 'VIRTUAL' ? 'VIRTUAL' : 'OTHER') === type)
				.map(asset => ({ ...asset, chartValue: Math.max(0, netAssetValue(asset, scope)) }))
				.filter(asset => asset.chartValue > 0)
		})).map(group => ({...group, value: group.items.reduce((sum, asset) => sum + asset.chartValue, 0)})).filter(group => group.value > 0);
		const total = groups.reduce((sum, group) => sum + group.value, 0);
		let angle = 0;
		return groups.map(group => {
			const start = angle;
			angle += group.value / total * Math.PI * 2;
			let childAngle = start;
			return {
				...group,
				path: ringPath(75, 123, start, angle),
				label: chartLabel(group.name, start, angle, 99, 12),
				children: group.items.map((asset, index) => {
					const childStart = childAngle;
					childAngle += asset.chartValue / total * Math.PI * 2;
					return {
						...asset,
						color: group.assetColors[index % group.assetColors.length],
						path: ringPath(126, 190, childStart, childAngle),
						label: chartLabel(asset.name, childStart, childAngle, 158, 10)
					};
				})
			};
		});
	}
	$: chartGroups = buildChart(assets, wealthScope);
	$: isAdmin = roles.includes('patrimonus-admin');
	$: applicationRoles = roles
		.filter((role) => role.startsWith('patrimonus-'))
		.map((role) => role.substring('patrimonus-'.length).toUpperCase());

	onMount(() => {
		loadBuildInfo();
		if (authenticated) {
			loadAssets();
		}

		updatePageFromLocation();
		window.addEventListener('hashchange', updatePageFromLocation);
		return () => window.removeEventListener('hashchange', updatePageFromLocation);
	});

	async function loadAssets() {
		loadingAssets = true;
		assetsError = null;
		try {
			assets = await getAssets();
			updatePageFromLocation();
		} catch (error) {
			assetsError = error instanceof Error ? error.message : String(error);
		} finally {
			loadingAssets = false;
		}
	}

	function updatePageFromLocation() {
		const hash = window.location.hash;
		currentPage = /#\/assets\/[^/]+\/ownership$/.test(hash) ? 'ownership-form'
			: /#\/assets\/[^/]+\/valuations\/(new|[^/]+)$/.test(hash) ? 'valuation-form'
			: /#\/assets\/[^/]+\/liabilities\/(new|[^/]+)$/.test(hash) ? 'liability-form'
			: hash === '#/assets/new' ? 'asset-create'
			: hash.startsWith('#/assets/') ? 'asset-edit'
			: hash === '#/profile/delete' ? 'profile-delete'
			: hash === '#/profile' ? 'profile' : 'assets';
		menuOpen = false;
		const match = hash.match(/^#\/assets\/([^/]+)(?:\/(valuations|liabilities)\/(new|[^/]+)|\/ownership)?$/);
		if (match && !editedAsset?.id?.toString().includes(match[1])) {
			const asset = assets.find(item => String(item.id) === match[1]);
			if (asset) {
				assetDeleteConfirmation = '';
				editedAsset = asset;
				editedAssetName = asset.name;
				editedAssetType = asset.type ?? '';
				editedProductType = asset.productType ?? '';
				editedAssetLocation = asset.location ?? '';
				loadValuations(asset.id);
				loadLiabilities(asset.id);
		loadOwnerships(asset.id);
			}
		}
		if (match?.[2]) {
			const records = match[2] === 'valuations' ? valuations : liabilities;
			const record = records.find(item => String(item.id) === match[3]);
			editingRecordId = match[3] === 'new' ? null : match[3];
			if (record) {
				if (match[2] === 'valuations') { valuationDate = record.date; valuationAmount = Math.round(Number(record.amount)); valuationCurrency = record.currency ?? assetCurrency(editedAsset); }
				else { liabilityDate = record.date; liabilityAmount = Math.round(Number(record.amount)); liabilityCurrency = record.currency ?? assetCurrency(editedAsset); }
			}
		}
	}

	async function loadOwnerships(id) {
		try { ownerships = await getOwnerships(id); ownershipError = null; }
		catch (error) { ownershipError = error instanceof Error ? error.message : String(error); }
	}

	async function saveOwners() {
		if (!editedAsset || savingOwnerships) return;
		const total = ownerships.reduce((sum, owner) => sum + Number(owner.sharePercent), 0);
		if (total > 100 || ownerships.filter(owner => owner.personal).length > 1 || ownerships.some(owner => !owner.ownerName.trim() || Number(owner.sharePercent) <= 0)) {
			ownershipError = 'Provide owner names, positive shares totaling at most 100%, and at most one personal owner.';
			return;
		}
		savingOwnerships = true; ownershipError = null;
		try {
			ownerships = await saveOwnerships(editedAsset.id, ownerships);
			assets = await getAssets();
			editedAsset = assets.find(asset => asset.id === editedAsset.id);
			showAssetEdit(editedAsset);
		} catch (error) { ownershipError = error instanceof Error ? error.message : String(error); }
		finally { savingOwnerships = false; }
	}

	async function loadValuations(id) {
		try { valuations = await getValuations(id); valuationError = null; updatePageFromLocation(); }
		catch (error) { valuationError = error instanceof Error ? error.message : String(error); }
	}

	async function saveValuation() {
		if (!editedAsset || !valuationDate || valuationAmount === '' || savingValuation) return;
		savingValuation = true; valuationError = null;
		try {
			if (editingRecordId) await updateValuation(editedAsset.id, editingRecordId, valuationDate, valuationAmount, valuationCurrency);
			else await addValuation(editedAsset.id, valuationDate, valuationAmount, valuationCurrency);
			await loadValuations(editedAsset.id);
			assets = await getAssets();
			editedAsset = assets.find(asset => asset.id === editedAsset.id);
			valuationAmount = '';
			showAssetEdit(editedAsset);
		} catch (error) { valuationError = error instanceof Error ? error.message : String(error); }
		finally { savingValuation = false; }
	}

	async function loadLiabilities(id) {
        try { liabilities = await getLiabilities(id); liabilityError = null; updatePageFromLocation(); }
        catch (error) { liabilityError = error instanceof Error ? error.message : String(error); }
    }

    async function saveLiability() {
        if (!editedAsset || !liabilityDate || liabilityAmount === '' || savingLiability) return;
        savingLiability = true; liabilityError = null;
        try {
            if (editingRecordId) await updateLiability(editedAsset.id, editingRecordId, liabilityDate, liabilityAmount, liabilityCurrency);
            else await addLiability(editedAsset.id, liabilityDate, liabilityAmount, liabilityCurrency);
            await loadLiabilities(editedAsset.id);
            assets = await getAssets();
            editedAsset = assets.find(asset => asset.id === editedAsset.id);
            liabilityAmount = '';
            showAssetEdit(editedAsset);
        } catch (error) { liabilityError = error instanceof Error ? error.message : String(error); }
        finally { savingLiability = false; }
    }

	function openOwnershipForm() {
		ownershipError = null;
		window.location.hash = `/assets/${editedAsset.id}/ownership`;
	}

	function openRecordForm(kind, record = null) {
		editingRecordId = record?.id ?? null;
		if (kind === 'valuations') {
			valuationDate = record?.date ?? new Date().toLocaleDateString('en-CA');
			valuationAmount = record ? Math.round(Number(record.amount)) : '';
			valuationCurrency = record?.currency ?? assetCurrency(editedAsset);
			valuationError = null;
		} else {
			liabilityDate = record?.date ?? new Date().toLocaleDateString('en-CA');
			liabilityAmount = record ? Math.round(Number(record.amount)) : '';
			liabilityCurrency = record?.currency ?? assetCurrency(editedAsset);
			liabilityError = null;
		}
		window.location.hash = `/assets/${editedAsset.id}/${kind}/${record?.id ?? 'new'}`;
	}

	function showAssetEdit(asset) {
		assetDeleteConfirmation = '';
		editedAsset = asset;
		editedAssetName = asset.name;
		editedAssetType = asset.type ?? '';
				editedProductType = asset.productType ?? '';
		editedAssetLocation = asset.location ?? '';
		valuations = [];
		liabilities = [];
		loadValuations(asset.id);
		loadLiabilities(asset.id);
		loadOwnerships(asset.id);
		editAssetError = null;
		window.location.hash = `/assets/${asset.id}`;
	}

	async function saveAsset() {
		const name = editedAssetName.trim();
		if (!editedAsset || !name || savingAsset) return;
		savingAsset = true; editAssetError = null;
		try {
			const saved = await updateAsset(editedAsset.id, name, editedAssetType || null, editedAssetType === 'PHYSICAL' ? editedAssetLocation.trim() : null, editedAssetType === 'VIRTUAL' ? editedProductType : null);
			assets = assets.map(asset => asset.id === saved.id ? saved : asset).sort((a,b) => a.name.localeCompare(b.name));
			showAssets();
		} catch (error) { editAssetError = error instanceof Error ? error.message : String(error); }
		finally { savingAsset = false; }
	}

	async function removeAsset() {
		if (!editedAsset || savingAsset || assetDeleteConfirmation !== editedAsset.name) return;
		savingAsset = true; editAssetError = null;
		try { await deleteAsset(editedAsset.id, assetDeleteConfirmation); assets = assets.filter(asset => asset.id !== editedAsset.id); showAssets(); }
		catch (error) { editAssetError = error instanceof Error ? error.message : String(error); }
		finally { savingAsset = false; }
	}

	function showAssetCreation() {
		window.location.hash = '/assets/new';
	}

	function showAssets() {
		menuOpen = false;
		window.location.hash = '';
		currentPage = 'assets';
	}

	function showProfile() {
		menuOpen = false;
		window.location.hash = '/profile';
		currentPage = 'profile';
	}

	function showDeleteConfirmation() {
		confirmation = '';
		deletionError = null;
		window.location.hash = '/profile/delete';
	}

	async function deleteAccount() {
		if (!username || confirmation !== username || deleting) return;
		deleting = true;
		deletionError = null;
		try {
			await deleteProfile(confirmation);
			clearAuthentication();
			authenticated = false;
			deleted = true;
			assets = [];
			window.location.hash = '';
		} catch (error) {
			deletionError = error instanceof Error ? error.message : String(error);
		} finally {
			deleting = false;
		}
	}

	async function addAsset() {
		const name = newAssetName.trim();
		if (!name || creatingAsset) return;

		creatingAsset = true;
		createAssetError = null;
		try {
			const asset = await createAsset(name, newAssetType || null, newAssetType === 'PHYSICAL' ? newAssetLocation.trim() : null, newAssetType === 'VIRTUAL' ? newProductType : null);
			assets = [...assets, asset].sort((left, right) => left.name.localeCompare(right.name));
			newAssetName = '';
			newAssetType = '';
			newProductType = '';
			newAssetLocation = '';
			showAssets();
		} catch (error) {
			createAssetError = error instanceof Error ? error.message : String(error);
		} finally {
			creatingAsset = false;
		}
	}

	async function loadBuildInfo() {
		const [webResult, apiResult] = await Promise.allSettled([
			getWebBuildInfo(),
			getApiBuildInfo()
		]);

		if (webResult.status === 'fulfilled') {
			webDockerBuildTimestamp = webResult.value.dockerBuildTimestamp ?? 'unknown';
		}
		if (apiResult.status === 'fulfilled') {
			apiDockerBuildTimestamp = apiResult.value.dockerBuildTimestamp ?? 'unknown';
		}
	}
</script>

<main>
	<h1>Patrimonus</h1>

	{#if deleted}
		<h2>Account deleted</h2>
		<p>Your Patrimonus assets and access have been removed.</p>
	{:else if authError}
		<p class="error">Authentication failed: {authError}</p>
		<button on:click={login}>Login</button>
	{:else if !authenticated}
		<p>Sign in to access Patrimonus.</p>
		<div class="actions">
			<button on:click={login}>Login</button>
			<button class="secondary" on:click={register}>Register</button>
		</div>
	{:else}
		<header>
			<div class="navigation">
				<button type="button" class="secondary menu-button" aria-label="Open navigation menu" aria-expanded={menuOpen}
					on:click={() => menuOpen = !menuOpen}>
					<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" fill="none" stroke="currentColor" stroke-width="2"/></svg>
				</button>
				{#if menuOpen}
					<nav class="menu" aria-label="Application navigation">
						<button type="button" on:click={showAssets}>Assets</button>
						<button type="button" on:click={showProfile}>Profile</button>
						<button type="button" on:click={logout}>Logout</button>
					</nav>
				{/if}
				<nav aria-label="Breadcrumb" class="breadcrumbs">
					<a href="#/" on:click={showAssets}>Assets</a>
					{#if currentPage === 'asset-create'}<span aria-current="page">/ Add asset</span>{/if}
					{#if currentPage === 'asset-edit'}<span aria-current="page">/ Edit asset</span>{/if}
					{#if currentPage === 'ownership-form'}
						<a href={`#/assets/${editedAsset?.id}`} on:click|preventDefault={() => showAssetEdit(editedAsset)}>/ Edit asset</a>
						<span aria-current="page">/ Ownership</span>
					{/if}
					{#if currentPage === 'valuation-form' || currentPage === 'liability-form'}
						<a href={`#/assets/${editedAsset?.id}`} on:click|preventDefault={() => showAssetEdit(editedAsset)}>/ Edit asset</a>
						<span aria-current="page">/ {editingRecordId ? 'Edit' : 'Add'} {currentPage === 'valuation-form' ? 'valuation' : 'liability'}</span>
					{/if}
					{#if currentPage === 'profile'}<span aria-current="page">/ Profile</span>{/if}
					{#if currentPage === 'profile-delete'}
						<a href="#/profile" on:click={showProfile}>/ Profile</a>
						<span aria-current="page">/ Delete account</span>
					{/if}
				</nav>
				{#if isAdmin}<span class="role">Admin</span>{/if}
			</div>
		</header>

		{#if currentPage === 'profile'}
			<section>
				<h2>Profile</h2>
				<p>Signed in as <strong>{username ?? 'Authenticated user'}</strong>.</p>
				<div class="danger-zone">
					<h3>Danger zone</h3>
					<p>Deleting your account permanently removes your assets and access to Patrimonus.</p>
					<button type="button" on:click={showDeleteConfirmation}>Delete my account</button>
				</div>
			</section>
		{:else if currentPage === 'profile-delete'}
			<section>
				<h2>Confirm account deletion</h2>
				<p>This permanently deletes your assets, then removes your identity from the Patrimonus Keycloak realm. Enter your account name <strong>{username ?? 'unavailable'}</strong> to confirm.</p>
				<label for="delete-confirmation">Confirmation</label>
				<input id="delete-confirmation" bind:value={confirmation} autocomplete="off" />
				{#if deletionError}<p class="error" role="alert">{deletionError}</p>{/if}
				<button type="button" disabled={!username || confirmation !== username || deleting} on:click={deleteAccount}>Permanently delete my account</button>
				<button type="button" class="secondary" on:click={showProfile}>Cancel</button>
			</section>
		{:else if currentPage === 'asset-create'}
			<section>
				<h2>Add asset</h2>
				<form on:submit|preventDefault={addAsset}>
					<label for="asset-name">Asset name</label>
					<input id="asset-name" bind:value={newAssetName} maxlength="200" required placeholder="e.g. Main residence" />
					<label for="asset-type">Type</label>
					<select id="asset-type" bind:value={newAssetType} required><option value="">Select a type</option><option value="VIRTUAL">Virtual</option><option value="PHYSICAL">Physical</option></select>
					{#if newAssetType === 'VIRTUAL'}
						<label for="asset-product">Financial product</label>
						<select id="asset-product" bind:value={newProductType} required><option value="">Select a product</option><option value="PEA">PEA</option><option value="PER">PER</option><option value="ASSURANCE_VIE">Assurance-vie</option><option value="FOREIGN_CURRENCY_ACCOUNT">Foreign currency account</option><option value="LIVRET">Livret</option><option value="SECURITIES_ACCOUNT">Securities account</option><option value="CRYPTO">Crypto</option><option value="SWISS_SECOND_PILLAR">Swiss 2nd pillar</option></select>
					{/if}
					{#if newAssetType === 'PHYSICAL'}
						<label for="asset-location">Location</label>
						<input id="asset-location" bind:value={newAssetLocation} maxlength="500" />
					{/if}
					{#if createAssetError}<p class="error">{createAssetError}</p>{/if}
					<div class="actions">
						<button type="submit" disabled={creatingAsset || !newAssetName.trim()}>{creatingAsset ? 'Adding…' : 'Add asset'}</button>
						<button class="secondary" type="button" on:click={showAssets}>Cancel</button>
					</div>
				</form>
			</section>
		{:else if currentPage === 'ownership-form' && editedAsset}
			<section>
				<h2>Ownership — {editedAsset.name}</h2>
				<p>If no owners are entered, personal ownership defaults to 100%.</p>
				{#each ownerships as owner, index}
					<div class="owner-entry">
						<input aria-label="Owner name" placeholder="Owner name" bind:value={owner.ownerName} maxlength="200" />
						<input aria-label="Ownership percentage" type="number" min="0.01" max="100" step="0.01" bind:value={owner.sharePercent} />
						<label><input type="checkbox" bind:checked={owner.personal} /> Mine</label>
						<button type="button" class="secondary" on:click={() => ownerships = ownerships.filter((_, i) => i !== index)}>Remove</button>
					</div>
				{/each}
				<button type="button" class="secondary" on:click={() => ownerships = [...ownerships, {ownerName:'', sharePercent:50, personal:false}]}>Add owner</button>
				{#if ownershipError}<p class="error" role="alert">{ownershipError}</p>{/if}
				<div class="actions"><button type="button" disabled={savingOwnerships} on:click={saveOwners}>Save</button><button type="button" class="secondary" on:click={() => showAssetEdit(editedAsset)}>Cancel</button></div>
			</section>
		{:else if (currentPage === 'valuation-form' || currentPage === 'liability-form') && editedAsset}
			<section>
				<h2>{editingRecordId ? 'Edit' : 'Add'} {currentPage === 'valuation-form' ? 'valuation' : 'liability'}</h2>
				{#if currentPage === 'valuation-form'}
					<form on:submit|preventDefault={saveValuation}>
						<label for="valuation-date">Valuation date</label>
						<input id="valuation-date" type="date" bind:value={valuationDate} required />
						<label for="valuation-amount">Amount ({valuationCurrency})</label>
						<input id="valuation-amount" type="number" min="0" step="1" bind:value={valuationAmount} required />
						<label for="valuation-currency">Currency</label>
						{#if isForeignCurrencyAccount(editedAsset)}
						<select id="valuation-currency" bind:value={valuationCurrency} required><option value="EUR">EUR</option><option value="CHF">CHF</option></select>
						{:else}
						<select id="valuation-currency" value={valuationCurrency} disabled><option value={valuationCurrency}>{valuationCurrency}</option></select>
						{/if}
						{#if valuationError}<p class="error" role="alert">{valuationError}</p>{/if}
						<div class="actions"><button type="submit" disabled={savingValuation || !valuationDate || valuationAmount === ''}>Save</button><button type="button" class="secondary" on:click={() => showAssetEdit(editedAsset)}>Cancel</button></div>
					</form>
				{:else}
					<form on:submit|preventDefault={saveLiability}>
						<label for="liability-date">Liability date</label>
						<input id="liability-date" type="date" bind:value={liabilityDate} required />
						<label for="liability-amount">Outstanding amount ({liabilityCurrency})</label>
						<input id="liability-amount" type="number" min="0" step="1" bind:value={liabilityAmount} required />
						<label for="liability-currency">Currency</label>
						{#if isForeignCurrencyAccount(editedAsset)}
						<select id="liability-currency" bind:value={liabilityCurrency} required><option value="EUR">EUR</option><option value="CHF">CHF</option></select>
						{:else}
						<select id="liability-currency" value={liabilityCurrency} disabled><option value={liabilityCurrency}>{liabilityCurrency}</option></select>
						{/if}
						{#if liabilityError}<p class="error" role="alert">{liabilityError}</p>{/if}
						<div class="actions"><button type="submit" disabled={savingLiability || !liabilityDate || liabilityAmount === ''}>Save</button><button type="button" class="secondary" on:click={() => showAssetEdit(editedAsset)}>Cancel</button></div>
					</form>
				{/if}
			</section>
		{:else if currentPage === 'asset-edit' && editedAsset}
			<section>
				<h2>Edit asset</h2>
				<form id="edit-asset-form" on:submit|preventDefault={saveAsset}>
					<label for="edit-asset-name">Asset name</label>
					<input id="edit-asset-name" bind:value={editedAssetName} maxlength="200" required />
					<strong>Type</strong>
					<p id="edit-asset-type">{editedAssetType === 'VIRTUAL' ? 'Virtual' : 'Physical'}</p>
					{#if editedAssetType === 'VIRTUAL'}
						<label for="edit-asset-product">Financial product</label>
						<select id="edit-asset-product" bind:value={editedProductType} required><option value="">Select a product</option><option value="PEA">PEA</option><option value="PER">PER</option><option value="ASSURANCE_VIE">Assurance-vie</option><option value="FOREIGN_CURRENCY_ACCOUNT">Foreign currency account</option><option value="LIVRET">Livret</option><option value="SECURITIES_ACCOUNT">Securities account</option><option value="CRYPTO">Crypto</option><option value="SWISS_SECOND_PILLAR">Swiss 2nd pillar</option></select>
					{/if}
					{#if editedAssetType === 'PHYSICAL'}
						<label for="edit-asset-location">Location</label>
						<input id="edit-asset-location" bind:value={editedAssetLocation} maxlength="500" />
					{/if}
					{#if editAssetError}<p class="error">{editAssetError}</p>{/if}
				</form>
				<div class="asset-section-heading"><h3>Ownership</h3><button class="add-record" type="button" on:click={openOwnershipForm} aria-label="Edit ownership" title="Edit ownership"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L9 17l-4 1 1-4Z"/></svg></button></div>
				<p>{ownerships.length ? ownerships.map(owner => `${owner.ownerName}: ${owner.sharePercent}%${owner.personal ? ' (Mine)' : ''}`).join(' · ') : '100% personal (default)'}</p>
				<div class="asset-section-heading"><h3>Valuations</h3><button class="add-record" type="button" on:click={() => openRecordForm('valuations')} aria-label="Add valuation" title="Add valuation"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button></div>
				<p>Current value: {editedAsset.latestValuation ? formatEuro(editedAsset.latestValuation.amount, editedAsset.latestValuation.currency) : 'Not valued'}</p>
				{#if valuationError}<p class="error">{valuationError}</p>{/if}
				<ul>{#each valuations as valuation}<li>{valuation.date}: <a href={`#/assets/${editedAsset.id}/valuations/${valuation.id}`} on:click|preventDefault={() => openRecordForm('valuations', valuation)}>{formatEuro(valuation.amount, valuation.currency)}</a></li>{/each}</ul>
                <div class="asset-section-heading"><h3>Liabilities</h3><button class="add-record" type="button" on:click={() => openRecordForm('liabilities')} aria-label="Add liability" title="Add liability"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button></div>
                <p>Outstanding liability: {editedAsset.latestLiability ? formatEuro(editedAsset.latestLiability.amount, editedAsset.latestLiability.currency) : 'No liability recorded'}</p>
                {#if liabilityError}<p class="error">{liabilityError}</p>{/if}
                <ul>{#each liabilities as liability}<li>{liability.date}: <a href={`#/assets/${editedAsset.id}/liabilities/${liability.id}`} on:click|preventDefault={() => openRecordForm('liabilities', liability)}>{formatEuro(liability.amount, liability.currency)}</a></li>{/each}</ul>
				<div class="actions">
					<button type="submit" form="edit-asset-form" disabled={savingAsset || !editedAssetName.trim()}>Save</button>
					<button type="button" class="secondary" on:click={showAssets}>Cancel</button>
				</div>
				<div class="danger-zone">
					<h3>Danger zone</h3>
					<p>Deleting this asset is permanent. Enter <strong>{editedAsset.name}</strong> to confirm.</p>
					<label for="asset-delete-confirmation">Asset name</label>
					<input id="asset-delete-confirmation" bind:value={assetDeleteConfirmation} autocomplete="off" />
					<button type="button" class="danger" disabled={savingAsset || assetDeleteConfirmation !== editedAsset.name} on:click={removeAsset}>Delete asset</button>
				</div>
			</section>
		{:else}
			<section>
				<div class="section-heading">
					<h2>Assets</h2>
					<button class="add-asset" type="button" on:click={showAssetCreation} aria-label="Add asset" title="Add asset">+</button>
				</div>
				<label for="wealth-scope">Wealth scope</label>
				<select id="wealth-scope" bind:value={wealthScope}><option value="personal">Personal</option><option value="household">Household</option></select>
				{#if loadingAssets}
					<p>Loading…</p>
				{:else if assetsError}
					<p class="error">{assetsError}</p>
					<button class="secondary" on:click={loadAssets}>Retry</button>
				{:else if assets.length === 0}
					<p>No assets.</p>
				{:else}
					{#if chartGroups.length}
						<div class="wealth-chart" aria-label="Net wealth breakdown by asset type and asset">
							<svg viewBox="0 0 400 400" role="img" aria-label="Inner ring: asset types; outer ring: assets. Segment sizes reflect positive net values.">
								{#each chartGroups as group}
									<path d={group.path} fill={group.color} stroke="white" stroke-width="2"><title>{group.name}: {formatEuro(group.value)}</title></path>
									<text x={group.label.x} y={group.label.y} text-anchor="middle" dominant-baseline="middle" font-size={group.label.fontSize} class="chart-label">{group.label.text}</text>
									{#each group.children as asset}
										<a href={`#/assets/${asset.id}`} on:click|preventDefault={() => showAssetEdit(asset)}>
											<path d={asset.path} fill={asset.color} stroke="white" stroke-width="2"><title>{asset.name}: {formatEuro(asset.chartValue)}</title></path>
											<text x={asset.label.x} y={asset.label.y} text-anchor="middle" dominant-baseline="middle" font-size={asset.label.fontSize} class="chart-label">{asset.label.text}</text>
										</a>
									{/each}
								{/each}
								<text x="200" y="194" text-anchor="middle" font-size="13" fill="currentColor">Net wealth</text>
								<text x="200" y="218" text-anchor="middle" font-size="19" font-weight="bold" fill="currentColor">{formatEuro(totalNetWealth)}</text>
							</svg>
						</div>
					{/if}
					<ul>
						{#each assets as asset}
							<li><a href={`#/assets/${asset.id}`} on:click|preventDefault={() => showAssetEdit(asset)}>{asset.name}</a> — {asset.latestValuation || asset.latestLiability ? formatEuro(netAssetValue(asset, wealthScope)) : 'Not valued'}{#if isValuationStale(asset.latestValuation?.date)} <svg class="stale-warning" role="img" aria-label={`Valuation dated ${asset.latestValuation.date}, more than one year old`} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><title>Valuation dated {asset.latestValuation.date} — more than one year old</title><path d="M12 3 2 21h20L12 3Z"/><path d="M12 9v5m0 3h.01"/></svg>{/if}</li>
						{/each}
						</ul>
					<p class="wealth-total"><span>Total net wealth</span><strong>{formatEuro(totalNetWealth)}</strong></p>
					{/if}
			</section>
		{/if}
	{/if}
</main>

<footer>
	<div class="build-info">
		<div><span>Web Docker build:</span> <em>{webDockerBuildTimestamp}</em></div>
		<div><span>API Docker build:</span> <em>{apiDockerBuildTimestamp}</em></div>
	</div>
	{#if authenticated && !deleted}
		<div class="identity">
			<span>User:</span>
			<em>{username ?? 'unknown'}</em>
			<span>Roles:</span>
			{#each applicationRoles as role}
				<span class="role-badge">{role}</span>
			{/each}
		</div>
	{/if}
</footer>

<style>
	.stale-warning { color: #8a4b00; font-size: 0.9em; }
	.wealth-chart { max-width: 33rem; margin: 1rem auto 1.5rem; text-align: center; }
	.wealth-chart svg { display: block; width: 100%; height: auto; }
	.wealth-chart svg a { cursor: pointer; }
	.chart-label { fill: white; pointer-events: none; font-weight: 600; }
	:global(body) {
		margin: 0;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		font-family: system-ui, sans-serif;
		background: #f5f5f5;
		color: #222;
	}

	main {
		width: min(48rem, calc(100% - 4rem));
		box-sizing: border-box;
		margin: 4rem auto;
		padding: 2rem;
		background: white;
		border-radius: 0.5rem;
		box-shadow: 0 0.25rem 1rem rgb(0 0 0 / 8%);
		flex: 1 0 auto;
	}

	header,
	.actions {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
	}

	.actions {
		justify-content: flex-start;
	}

	.navigation { display: flex; align-items: center; gap: 0.6rem; position: relative; }
	.menu-button { display: inline-flex; align-items: center; justify-content: center; }
	.menu {
		position: absolute;
		top: 100%;
		left: 0;
		z-index: 2;
		display: grid;
		min-width: 10rem;
		padding: 0.4rem;
		background: white;
		border: 1px solid #ccc;
		border-radius: 0.3rem;
		box-shadow: 0 4px 12px #0002;
	}
	.menu button { background: transparent; text-align: left; }
	.breadcrumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; margin: 0; }
	.danger-zone { padding: 1rem; margin-top: 2rem; border: 1px solid #ad3030; border-radius: 0.4rem; }
	.danger-zone h3 { color: #ad3030; }
	#delete-confirmation { display: block; margin: 0.5rem 0 1rem; max-width: 100%; }

	.section-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
	}

	.section-heading h2 {
		margin-bottom: 1rem;
	}

	.asset-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
	.add-record { display: inline-flex; align-items: center; justify-content: center; padding: 0.35rem; min-width: 2.25rem; min-height: 2.25rem; }
	.add-asset {
		min-width: 2.25rem;
		padding: 0.35rem 0.65rem;
		font-size: 1.3rem;
		font-weight: 700;
		line-height: 1;
	}

	button {
		border: 0;
		border-radius: 0.3rem;
		padding: 0.65rem 1rem;
		font: inherit;
		cursor: pointer;
	}

	button.secondary {
		background: #e8e8e8;
	}

	.role {
		margin-left: 0.75rem;
		font-size: 0.8rem;
		font-weight: 600;
	}

	.error {
		font-weight: 600;
	}

	form {
		margin: 1rem 0 1.5rem;
	}

	label {
		margin-bottom: 0.35rem;
		font-weight: 600;
	}

	.owner-entry { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem; margin: 0.75rem 0; }
	.owner-entry input[type="number"] { width: 6rem; }
	.owner-entry input[type="text"] { min-width: 9rem; }
	.asset-entry {
		display: flex;
		align-items: stretch;
		gap: 0.6rem;
	}

	.asset-entry input {
		flex: 1;
		min-width: 0;
		margin: 0;
	}

	.asset-entry button {
		margin: 0;
	}

	footer {
		box-sizing: border-box;
		width: 100%;
		border-top: 1px solid #d8d8d8;
		padding: 0.8rem 1rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		background: white;
		color: #666;
		font-size: 0.78rem;
	}

	.build-info {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	.identity {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		flex-wrap: wrap;
		gap: 0.4rem;
	}

	.role-badge {
		display: inline-block;
		padding: 0.14rem 0.42rem;
		border-radius: 0.25rem;
		background: #6c757d;
		color: white;
		font-size: 0.68rem;
		font-weight: 700;
		line-height: 1.2;
	}

	.wealth-total { display: flex; justify-content: space-between; gap: 1rem; border-top: 2px solid #ddd; padding-top: 1rem; font-size: 1.1rem; }
	.wealth-total strong { text-align: right; }
	@media (max-width: 640px) {
		main { width: calc(100% - 1rem); padding: 1.5rem; }
		section { line-height: 1.6; }
		form { display: flex; flex-direction: column; gap: 0.65rem; }
		form input, form select { box-sizing: border-box; width: 100%; min-height: 44px; font: inherit; padding: 0.6rem; }
		button { min-height: 44px; }
		.actions { flex-wrap: wrap; gap: 0.75rem; }
		section li { padding: 0.45rem 0; overflow-wrap: anywhere; }
		.wealth-total { flex-wrap: wrap; }

		main {
			width: calc(100% - 2rem);
			margin: 1rem auto;
			padding: 1.25rem;
		}

		.owner-entry { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem; margin: 0.75rem 0; }
	.owner-entry input[type="number"] { width: 6rem; }
	.owner-entry input[type="text"] { min-width: 9rem; }
	.asset-entry {
			align-items: stretch;
			flex-direction: column;
		}

		footer {
			align-items: flex-start;
			flex-direction: column;
		}

		.identity {
			justify-content: flex-start;
		}
	}
</style>
